import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchProject, updateProjectText } from '../../shared/api/textProjectApi';
import { useAppDispatch } from '../../store/store';
import { fetchImageProject, updateLayoutImage } from '../../shared/api/imageProjectApi';
import s from './CreatePostPage.module.css';
import LogoComponent from '../../shared/ui/logo/LogoComponent';
import Button from '../../shared/ui/button/Button';

import { CanvasContainerContext } from '../../shared/context/CanvasContainerContext';
import Canvas from '../image-editor-page/widgets/canvas/Canvas';
import { useSelector } from 'react-redux';
import { selectActiveEditorMode } from '../../store/selectors/pageSelectors';

import {
    selectCurrentProject,
    selectScriptData,
    selectSelectedSocialMedia,
    selectIsLoadingScript,
} from '../../store/selectors/projectSelectors';
import { formatScriptData, getShortTextFromHtml } from '../../shared/helpers/transformData';
import { useDebounce } from '../../shared/hooks/useDebounce';
import Loader from '../../shared/ui/loader/Loader';
import {
    selectImageLayout,
    selectLoading,
    selectLoadingDownload,
    selectSelectedImage,
    selectHasChanged,
} from '../../store/selectors/imageSelectors';

import { setHasChanged, setImageProject, setSelectedImage } from '../../store/slices/imageSlice';
import { PostModal } from '../../shared/ui/post-modal/PostModal';

import { selectImageProject } from '../../store/selectors/imageSelectors';
import { selectUser } from '../../store/selectors/authSelectors';
import UserProfileButton from '../../shared/ui/user-profile-button/UserProfileButton';
import InputGenerate from '../custom-post-page/widgets/input-generate/InputGenerate';
import { toggleActivePrompt } from '../../store/slices/pageSlice';
import DisplayEditSettings from '../custom-post-page/widgets/display-edit-settings/DisplayEditSettings';
import {
    setCurrentProject,
    setGenerationMode,
    setImageData,
    setScriptData,
    setSelectedSocialMedia,
    setVideoData,
} from '../../store/slices/projectSlice';
import { setProjectNotes } from '../../store/slices/notesSlice';
import IconEdit from '../../shared/ui/icons/IconEdit';
import { handleSaveImage } from '../../shared/utils/handleSaveImage';
import { uploadCanvasImage } from '../../shared/helpers/downloadCanvas';
import PostButton from '../../shared/ui/post-button/PostButton';

const CustomPostPage = () => {
    const [newImageWithEffects, setNewImageWithEffects] = useState<File | null>(null);

    const { projectId } = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [value, setValue] = useState<string>('');

    const containerRef = useRef<HTMLDivElement>(null);
    const isFirstDebounce = useRef(true);
    const activeEditorMode = useSelector(selectActiveEditorMode);
    const scriptData = useSelector(selectScriptData);
    const currentProject = useSelector(selectCurrentProject);
    const imageProject = useSelector(selectImageProject);
    const imageLayout = useSelector(selectImageLayout);
    const debouncedValue = useDebounce(value, 2000);
    const selectedImage = useSelector(selectSelectedImage);
    const [showPostModal, setShowPostModal] = useState(false);
    const content_type = currentProject?.content_type;
    const selectedSocialMedia = useSelector(selectSelectedSocialMedia);
    const loadingDownload = useSelector(selectLoadingDownload);
    const isLoadingScript = useSelector(selectIsLoadingScript);
    const loadingImage = useSelector(selectLoading);
    const [isLoadingSaveFrame, setIsLoadingSaveFrame] = useState(false);
    const user = useSelector(selectUser);
    const hasChanged = useSelector(selectHasChanged);
    useEffect(() => {
        dispatch(setGenerationMode('text_image'));
    }, []);

    useEffect(() => {
        dispatch(setSelectedSocialMedia(content_type?.toLowerCase() || ''));
    }, [content_type]);

    console.log(selectedSocialMedia, 'selectedSocialMedia');
    console.log(content_type, 'content_type');

    useEffect(() => {
        if (
            imageLayout &&
            projectId &&
            imageProject &&
            imageProject.allImagesForLayouts.length === 1
        ) {
            const imageData = imageProject?.allImagesForLayouts[0];

            if (imageData) {
                dispatch(setSelectedImage({ image: imageData, layoutId: imageLayout.id }));
            }
        }
    }, [dispatch, imageProject]);

    useEffect(() => {
        if (projectId) {
            const fetchData = async () => {
                const project = await dispatch(fetchProject({ projectId: projectId })).unwrap();

                const imageProjectId = project.image?.id;
                if (imageProjectId) {
                    await dispatch(fetchImageProject(imageProjectId)).unwrap();
                }
            };
            fetchData();
            dispatch(toggleActivePrompt('create_post'));
        }
    }, [dispatch, projectId]);

    useEffect(() => {
        if (scriptData) {
            (async () => {
                const formattedText = await formatScriptData(scriptData);
                const shortText = await getShortTextFromHtml(formattedText);
                setValue(shortText);
            })();
        }
    }, [scriptData]);

    const handleSetValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
    };

    useEffect(() => {
        if (debouncedValue.trim() === '' || !currentProject?.id) {
            return;
        }
        if (isFirstDebounce.current) {
            isFirstDebounce.current = false;
            return;
        }
        dispatch(updateProjectText({ projectId: currentProject.id, body: debouncedValue }));
    }, [debouncedValue, currentProject?.id, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(toggleActivePrompt(null));
            dispatch(setSelectedImage({ image: null, layoutId: null }));
            dispatch(setCurrentProject(null));
            dispatch(setImageData(null));
            dispatch(setScriptData(null));
            dispatch(setProjectNotes([]));
            dispatch(setImageProject(null));
            dispatch(toggleActivePrompt(null));
            dispatch(setSelectedSocialMedia(null));
            dispatch(setVideoData(null));
            dispatch(setGenerationMode(null));
        };
    }, []);

    const backNavigation = () => {
        navigate(-1);
    };

    const saveImage = async ({ source }: { source: 'generate' | 'post' }) => {
        if (hasChanged) {
            if (selectedImage?.image) {
                if (source === 'post') {
                    setIsLoadingSaveFrame(true);
                }

                dispatch(setHasChanged(false));

                await handleSaveImage({
                    activePrompt: 'create_post',
                    selectedImage,
                    dispatch,
                    newImageWithEffects,
                });
                if (containerRef.current && imageLayout?.id) {
                    const canvasEl = containerRef.current.querySelector(
                        'canvas',
                    ) as HTMLCanvasElement;
                    if (canvasEl) {
                        const file = await uploadCanvasImage(containerRef);
                        if (file) {
                            await dispatch(updateLayoutImage({ layoutId: imageLayout.id, file }));
                        }
                    }
                }
                if (source === 'post') {
                    setIsLoadingSaveFrame(false);
                    setShowPostModal(true);
                }
            }
        }
    };

    return imageProject !== null ? (
        <CanvasContainerContext.Provider value={containerRef}>
            <div className={s.container}>
                <div className={s.top_container}>
                    <LogoComponent />
                    <div className={s.top_right_container}>
                        {activeEditorMode !== 'Review' && (
                            <InputGenerate
                                saveImage={() => saveImage({ source: 'generate' })}
                                activeEditorMode={activeEditorMode}
                                currentProject={currentProject}
                                imageProject={imageProject}
                                selectedImage={selectedImage}
                            />
                        )}
                        <UserProfileButton />
                    </div>
                </div>
                <div className={s.center_container}>
                    <div className={s.left_center_container}>
                        <h3 className={s.title}>New Post</h3>
                        <p className={s.subtitle}>Please select the platform you want to post in</p>
                        <DisplayEditSettings />
                    </div>
                    <div className={s.right_center_container}>
                        <Canvas
                            ref={containerRef}
                            setNewImageWithEffects={setNewImageWithEffects}
                        />

                        <div
                            className={s.edit_post_button}
                            onClick={() => navigate(`/custom-post/${projectId}`)}
                        >
                            <IconEdit />
                        </div>
                        <input
                            type="text"
                            className={s.input}
                            value={value}
                            onChange={handleSetValue}
                        />

                        {isLoadingScript && <Loader text="Generating... " blur={true} />}
                        {loadingImage && (
                            <Loader text="Our AI is customizing your image..." blur={true} />
                        )}
                        {isLoadingSaveFrame && (
                            <Loader text="Saving your image before post..." blur={true} />
                        )}
                    </div>
                </div>
                <div className={s.bottom_container}>
                    <Button
                        text="Back"
                        onClick={backNavigation}
                        style={{
                            width: '100px',
                            fontWeight: '600',
                        }}
                    />

                    <PostButton
                        currentProject={currentProject}
                        activeEditorMode={activeEditorMode}
                        user={user}
                        saveImage={() => saveImage({ source: 'post' })}
                    />
                </div>
            </div>

            <PostModal isOpen={showPostModal} onClose={() => setShowPostModal(false)} />
            {loadingDownload && <Loader />}
        </CanvasContainerContext.Provider>
    ) : (
        <Loader />
    );
};

export default CustomPostPage;
